import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as dmsService from "../../../services/dmsService";
import * as finService from "../../../services/finService";
import useFinOptions from "../../Fin/useFinOptions";
import { fmtMoney } from "../../Fin/gl/glContext";
import { DataTable, hint, linkBtn } from "../../Fin/gl/glUi";
import ReceiptForm from "../../Fin/cash/ReceiptForm";
import { ActionHistory, ChecklistForm, DealEditor, DmsBadge, DocHead, Panel, Section, Totals, dealPayload, emptyDeal, run } from "./dmsUi";
import { AppDropdown, EmptyState, Field, FormDrawer, LoadState, PageHeader, PrimaryButton, SecondaryButton, formatDate, inputStyle, pageStyle, todayYmd, useLoad } from "../../../components/ui";

// Sales orders (Dealership → Sales Orders): the client manual's order screen. Availability /
// Reserve chassis, Book (credit and discount checked), Registration & Warranty, Generate Delivery,
// Delivery Checklist, Create Delivery, Create AR Invoice, cancel line / order, PDF, Action History.
const STATUS = [
  ["", "All open"],
  ["ENTERED", "Entered"],
  ["PENDING_APPROVAL", "Pending approval"],
  ["BOOKED", "Booked"],
  ["PARTIALLY_DELIVERED", "Part delivered"],
  ["DELIVERED", "Delivered"],
  ["INVOICED", "Invoiced"],
  ["CLOSED", "Closed"],
  ["CANCELLED", "Cancelled"],
].map(([value, label]) => ({ value, label }));

function useLookup(type) {
  const { data } = useLoad(() => dmsService.listLookups({ lookup_type: type, status: "ACTIVE" }), [type]);
  return (data || []).map((r) => ({ label: r.name, value: r.code }));
}

/* ------------------------------ availability ------------------------------ */

function Availability({ so, line, onHide, onDone }) {
  const { data, loading, error, reload } = useLoad(() => dmsService.availableForLine(so.id, line.id), [line.id]);
  return (
    <Panel title={`Availability – line ${line.line_no}: ${line.variant_name}`} onHide={onHide} width={860}>
      <div style={{ ...hint, marginBottom: 12 }}>In-stock vehicles of this variant: the ordered colour first, then PDI passed, then the oldest stock.</div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          minWidth={700}
          empty={<EmptyState title="Nothing in stock" text="Transfer one from another location, or order from the manufacturer." />}
          columns={[
            { label: "VIN", render: (u) => <b>{u.vin}</b> },
            { label: "Colour", render: (u) => `${u.ext_colour_name || "—"}${u.colour_match ? "" : " (other colour)"}` },
            { label: "Location", render: (u) => (u.location_code === so.location_code ? "This showroom" : u.location_name || "Other location") },
            { label: "PDI", render: (u) => <DmsBadge status={u.pdi_status === "PASSED" ? "PASSED" : u.pdi_status} /> },
            { label: "Age", num: true, render: (u) => (u.age_days === null ? "—" : `${u.age_days} d`) },
            {
              label: "",
              render: (u) => (
                <button
                  type="button"
                  style={linkBtn}
                  onClick={() =>
                    run(
                      () => dmsService.lineAction(so.id, line.id, "reserve", { unit_id: u.id }),
                      () => {
                        onDone();
                        onHide();
                      }
                    )
                  }
                >
                  Reserve
                </button>
              ),
            },
          ]}
        />
      </LoadState>
    </Panel>
  );
}

/* ------------------------------ delivery ------------------------------ */

function DeliveryView({ id, onHide, onChanged }) {
  const { data: d, loading, error, reload } = useLoad(() => dmsService.getDelivery(id), [id]);
  const [odo, setOdo] = useState({});
  const refresh = () => {
    reload();
    onChanged();
  };
  return (
    <Panel title={d ? `Delivery ${d.delivery_number}` : "Delivery"} onHide={onHide} width={900}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {d ? (
          <>
            <DocHead title="Order" number={d.so_number} status={d.status} sub={`${d.customer_name} · ${formatDate(d.delivery_date)} · received by ${d.received_by_name || "—"}`}>
              {d.status === "DRAFT" ? (
                <PrimaryButton onClick={() => run(() => dmsService.deliveryAction(id, "post", { lines: d.lines.map((l) => ({ so_line_id: l.so_line_id, odometer: odo[l.so_line_id] !== undefined ? Number(odo[l.so_line_id]) : undefined })) }), refresh)}>Create delivery</PrimaryButton>
              ) : null}
              {d.status === "POSTED" && !d.invoice ? <PrimaryButton onClick={() => run(() => dmsService.deliveryAction(id, "invoice"), refresh)}>Create AR invoice</PrimaryButton> : null}
              {d.invoice ? (
                <SecondaryButton onClick={() => finService.transactionPdf(d.invoice.ar_trx_id, `${d.invoice.trx_number}.pdf`)}>Tax invoice {d.invoice.trx_number}</SecondaryButton>
              ) : null}
              <SecondaryButton onClick={() => dmsService.deliveryPdf(id, `${d.delivery_number}.pdf`)}>Gate pass PDF</SecondaryButton>
              {d.status === "DRAFT" ? <SecondaryButton onClick={() => window.confirm("Cancel this delivery?") && run(() => dmsService.deliveryAction(id, "cancel"), refresh)}>Cancel</SecondaryButton> : null}
            </DocHead>
            <Section title="Vehicles">
              <DataTable
                rows={d.lines}
                minWidth={640}
                columns={[
                  { label: "#", render: (l) => l.line_no },
                  { label: "Vehicle", render: (l) => l.description },
                  { label: "VIN", render: (l) => l.vin },
                  { label: "Plate", render: (l) => [l.plate_number, l.plate_emirate].filter(Boolean).join(" ") || "—" },
                  {
                    label: "Odometer",
                    render: (l) =>
                      d.status === "DRAFT" ? (
                        <input type="number" min={0} className="form-control form-control-sm" style={{ width: 110 }} aria-label="Odometer" value={odo[l.so_line_id] ?? ""} onChange={(e) => setOdo({ ...odo, [l.so_line_id]: e.target.value })} />
                      ) : (
                        l.odometer ?? "—"
                      ),
                  },
                ]}
              />
            </Section>
            <Section title="Delivery checklist">
              <ChecklistForm checklist={d.checklist} disabled={d.status !== "DRAFT"} onSave={(body) => run(() => dmsService.deliveryAction(id, "checklist", body), reload)} />
            </Section>
            <Section title="Action history">
              <ActionHistory actions={d.actions} />
            </Section>
          </>
        ) : null}
      </LoadState>
    </Panel>
  );
}

/* ------------------------------ order view ------------------------------ */

function OrderView({ id, onHide, onChanged }) {
  const { data: so, loading, error, reload } = useLoad(() => dmsService.getOrder(id), [id]);
  const credit = useLoad(() => dmsService.creditCheck(id), [id]);
  const cancelReasons = useLookup("CANCEL_REASON");
  const [avail, setAvail] = useState(null);
  const [reg, setReg] = useState(null);
  const [delivery, setDelivery] = useState(null);
  const [form, setForm] = useState(null);
  const refresh = () => {
    reload();
    credit.reload();
    onChanged();
  };
  const open = so && ["ENTERED", "PENDING_APPROVAL", "BOOKED"].includes(so.status);
  const reserved = so ? so.lines.filter((l) => l.status === "RESERVED") : [];
  return (
    <Panel title={so ? `Sales order ${so.so_number}` : "Sales order"} onHide={onHide} width={1150}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {so ? (
          <>
            <DocHead title="" number={so.customer_name} status={so.status} sub={`${so.account ? so.account.account_number : ""} · ${so.location_name || ""} · ${so.salesperson_name || ""} · ${formatDate(so.order_date)}${so.quote_number ? ` · quotation ${so.quote_number}` : ""}${so.customer_po ? ` · PO ${so.customer_po}` : ""}`}>
              {so.status === "ENTERED" ? <PrimaryButton onClick={() => run(() => dmsService.orderAction(id, "book"), refresh)}>Book</PrimaryButton> : null}
              {["BOOKED", "PARTIALLY_DELIVERED", "INVOICED"].includes(so.status) && reserved.length ? <PrimaryButton onClick={() => setForm("delivery")}>Generate delivery</PrimaryButton> : null}
              {!["CANCELLED", "CLOSED"].includes(so.status) ? <SecondaryButton onClick={() => setForm("advance")}>Receive advance</SecondaryButton> : null}
              {["ENTERED", "BOOKED"].includes(so.status) ? <SecondaryButton onClick={() => setForm("cancel")}>Cancel order</SecondaryButton> : null}
              <SecondaryButton onClick={() => dmsService.orderPdf(id, `${so.so_number}.pdf`)}>Order PDF</SecondaryButton>
            </DocHead>
            {so.status === "ENTERED" && credit.data && !credit.data.ok ? <div style={{ color: "#991B1B", fontSize: 13, marginBottom: 12 }}>Cannot book yet: {credit.data.reason}</div> : null}
            {credit.data && credit.data.ok && so.account && !so.account.is_cash ? (
              <div style={{ ...hint, marginBottom: 12 }}>
                Credit customer: limit {fmtMoney(credit.data.limit)}, open balance {fmtMoney(credit.data.exposure)}
              </div>
            ) : null}
            <Section title="Vehicles">
              <DataTable
                rows={so.lines}
                minWidth={980}
                columns={[
                  { label: "#", render: (l) => l.line_no },
                  { label: "Vehicle", render: (l) => <div><b>{l.make_name} {l.model_name}</b> {l.variant_name}<div style={hint}>{[l.ext_colour_name, l.stock_type.toLowerCase()].filter(Boolean).join(" · ")}</div></div> },
                  { label: "Chassis", render: (l) => (l.vin ? <span>{l.vin}{l.pdi_status !== "PASSED" ? <span style={hint}> (PDI {String(l.pdi_status).toLowerCase().replace(/_/g, " ")})</span> : null}</span> : "—") },
                  { label: "Registration", render: (l) => (l.registration ? l.registration.plate_number || "Not required" : "—") },
                  { label: "Net", num: true, render: (l) => fmtMoney(l.net_amount) },
                  { label: "Total", num: true, render: (l) => fmtMoney(l.line_total) },
                  { label: "Status", render: (l) => <DmsBadge status={l.status} /> },
                  {
                    label: "",
                    render: (l) => (
                      <span className="d-inline-flex flex-wrap gap-2">
                        {open && l.status === "OPEN" ? (
                          <button type="button" style={linkBtn} onClick={() => setAvail(l)}>
                            Availability
                          </button>
                        ) : null}
                        {open && l.status === "RESERVED" ? (
                          <button type="button" style={linkBtn} onClick={() => window.confirm(`Release VIN ${l.vin}?`) && run(() => dmsService.lineAction(id, l.id, "unreserve"), refresh)}>
                            Unreserve
                          </button>
                        ) : null}
                        {["RESERVED", "DELIVERED", "INVOICED"].includes(l.status) && so.status !== "CANCELLED" ? (
                          <button type="button" style={linkBtn} onClick={() => setReg(l)}>
                            Registration
                          </button>
                        ) : null}
                        {["ENTERED", "BOOKED"].includes(so.status) && ["OPEN", "RESERVED"].includes(l.status) && so.lines.filter((x) => x.status !== "CANCELLED").length > 1 ? (
                          <button type="button" style={linkBtn} onClick={() => window.confirm("Cancel this line?") && run(() => dmsService.lineAction(id, l.id, "cancel"), refresh)}>
                            Cancel line
                          </button>
                        ) : null}
                      </span>
                    ),
                  },
                ]}
              />
              {so.charges.length ? (
                <div className="mt-3">
                  <div style={{ fontWeight: 600, fontSize: 14 }}>Services</div>
                  {so.charges.map((c) => (
                    <div key={c.id} className="d-flex justify-content-between" style={{ fontSize: 14, color: c.status === "CANCELLED" ? "#9CA3AF" : undefined }}>
                      <span>
                        {c.description}
                        {c.so_line_no ? ` (vehicle ${c.so_line_no})` : ""} <DmsBadge status={c.status} />
                      </span>
                      <span>{c.is_complimentary ? "Complimentary" : `${fmtMoney(c.amount)} + VAT ${fmtMoney(c.tax_amount)}`}</span>
                    </div>
                  ))}
                </div>
              ) : null}
              <div className="mt-3">
                <Totals
                  doc={so}
                  extra={[
                    ...(Number(so.trade_in_allowance) ? [["Less trade-in", `(${fmtMoney(so.trade_in_allowance)})`]] : []),
                    ...(Number(so.advance_received) ? [["Advance received", `(${fmtMoney(so.advance_received)})`]] : []),
                  ]}
                />
              </div>
            </Section>
            <div className="row">
              <div className="col-md-6">
                <Section
                  title="Deliveries"
                  right={
                    so.deliveries.filter((d) => d.status === "POSTED" && !Number(d.invoiced)).length > 1 ? (
                      <button type="button" style={linkBtn} onClick={() => run(() => dmsService.consolidatedInvoice(id), refresh)}>
                        Invoice posted deliveries together
                      </button>
                    ) : null
                  }
                >
                  {so.deliveries.length ? (
                    so.deliveries.map((d) => (
                      <div key={d.id} className="d-flex justify-content-between" style={{ fontSize: 14, marginBottom: 6 }}>
                        <button type="button" style={linkBtn} onClick={() => setDelivery(d.id)}>
                          {d.delivery_number}
                        </button>
                        <span>
                          {formatDate(d.delivery_date)} <DmsBadge status={d.status} />
                        </span>
                      </div>
                    ))
                  ) : (
                    <div style={hint}>Reserve the vehicles, book the order, then generate the delivery.</div>
                  )}
                </Section>
                <Section title="Invoices">
                  {so.invoices.length ? (
                    so.invoices.map((i) => (
                      <div key={i.trx_number} className="d-flex justify-content-between" style={{ fontSize: 14 }}>
                        <button type="button" style={linkBtn} onClick={() => finService.transactionPdf(i.ar_trx_id, `${i.trx_number}.pdf`)}>
                          {i.trx_class === "CM" ? "Credit memo" : "Invoice"} {i.trx_number}
                        </button>
                        <span>{fmtMoney(i.amount)}</span>
                      </div>
                    ))
                  ) : (
                    <div style={hint}>None yet.</div>
                  )}
                </Section>
                <Section title="Advances">
                  {so.advances && so.advances.length ? (
                    so.advances.map((r) => (
                      <div key={r.receipt_number} className="d-flex justify-content-between" style={{ fontSize: 14 }}>
                        <button type="button" style={linkBtn} onClick={() => finService.receiptPdf(r.id, `${r.receipt_number}.pdf`)}>
                          {r.receipt_number}
                        </button>
                        <span>
                          {fmtMoney(r.amount)} · {r.method_type.toLowerCase().replace(/_/g, " ")} <DmsBadge status={r.status === "REVERSED" ? "CANCELLED" : Number(r.unapplied_amount) ? "OPEN" : "CLOSED"} />
                        </span>
                      </div>
                    ))
                  ) : (
                    <div style={hint}>None. Advances are applied to the order's invoice.</div>
                  )}
                </Section>
                <Section title="Trade-ins">
                  {so.trade_ins.length ? (
                    so.trade_ins.map((t) => (
                      <div key={t.id} className="d-flex justify-content-between" style={{ fontSize: 14 }}>
                        <span>
                          {t.ti_number}: {t.make_name} {t.model_name} ({t.vin})
                        </span>
                        <span>
                          {fmtMoney(t.offered_allowance)} <DmsBadge status={t.status} />
                        </span>
                      </div>
                    ))
                  ) : (
                    <div style={hint}>Add one from Trade-ins (link it to this order).</div>
                  )}
                </Section>
              </div>
              <div className="col-md-6">
                <Section title="Action history">
                  <ActionHistory actions={so.actions} />
                </Section>
              </div>
            </div>
          </>
        ) : null}
      </LoadState>
      {avail ? <Availability so={so} line={avail} onHide={() => setAvail(null)} onDone={refresh} /> : null}
      {delivery ? <DeliveryView id={delivery} onHide={() => setDelivery(null)} onChanged={refresh} /> : null}
      <FormDrawer
        show={!!reg}
        title={reg ? `Registration & warranty – ${reg.vin || ""}` : ""}
        initial={reg && reg.registration ? { ...reg.registration, registration_required: !!reg.registration.registration_required } : { registration_required: true, registration_date: todayYmd(), warranty_start: todayYmd() }}
        onHide={() => setReg(null)}
        onDone={refresh}
        width={560}
        onSave={(clean, raw) => dmsService.lineAction(id, reg.id, "registration", { ...clean, registration_required: !!raw.registration_required })}
        fields={(v) => [
          { key: "registration_required", label: "Registration is done by us", type: "checkbox" },
          ...(v.registration_required
            ? [
                { key: "plate_number", label: "Plate number", required: true, max: 24 },
                { key: "plate_emirate", label: "Emirate", max: 60 },
                { key: "registration_date", label: "Registered on", type: "date", required: true },
                { key: "registration_expiry", label: "Registration expires", type: "date" },
              ]
            : []),
          { key: "insurance_company", label: "Insurance company", max: 100 },
          { key: "insurance_policy", label: "Policy number", max: 60 },
          { key: "insurance_expiry", label: "Insurance expires", type: "date" },
          { key: "warranty_booklet_no", label: "Warranty booklet number", max: 40 },
          { key: "warranty_start", label: "Warranty starts", type: "date" },
          { key: "warranty_end", label: "Warranty ends (blank: from the variant)", type: "date" },
          { key: "warranty_km", label: "Warranty km", type: "number", min: 0 },
        ]}
      />
      <FormDrawer
        show={form === "delivery"}
        title="Generate delivery"
        initial={{ delivery_date: todayYmd(), received_by_name: so ? so.customer_name : "" }}
        onHide={() => setForm(null)}
        onDone={(data) => {
          refresh();
          if (data && data.id) setDelivery(data.id);
        }}
        onSave={(clean) => dmsService.orderAction(id, "deliveries", clean)}
        fields={[
          { key: "delivery_date", label: "Delivery date", type: "date", required: true },
          { key: "received_by_name", label: "Received by", max: 150 },
          { key: "received_by_id_no", label: "Their ID / Emirates ID", max: 40 },
        ]}
      >
        <div style={hint}>Every reserved vehicle goes on this delivery.</div>
      </FormDrawer>
      {form === "advance" && so ? (
        <ReceiptForm
          show
          title={`Advance on ${so.so_number}`}
          hideCustomer
          hideLocation
          fixed={{ purpose: "ADVANCE" }}
          onHide={() => setForm(null)}
          onDone={refresh}
          save={(body) => dmsService.recordAdvance(id, { method_id: body.method_id, amount: body.amount, receipt_date: body.receipt_date, reference: body.reference, cheque: body.cheque, bank_account_id: body.bank_account_id, remarks: body.remarks })}
        />
      ) : null}
      <FormDrawer
        show={form === "cancel"}
        title="Cancel order"
        initial={{}}
        onHide={() => setForm(null)}
        onDone={refresh}
        onSave={(clean) => dmsService.orderAction(id, "cancel", clean)}
        fields={[
          { key: "reason", label: "Reason", type: "select", required: true, options: cancelReasons },
          { key: "remarks", label: "Remarks", type: "textarea", max: 1000 },
        ]}
      >
        {so && so.status === "BOOKED" ? <div style={hint}>A booked order's cancellation is approved by a sales manager.</div> : null}
      </FormDrawer>
    </Panel>
  );
}

/* ------------------------------ direct order ------------------------------ */

function NewOrder({ onHide, onSaved }) {
  const fin = useFinOptions(["locations"]);
  const customers = useLoad(() => finService.listCustomers({ size: 200 }), []);
  const [head, setHead] = useState({ customer_account_id: null, location_id: null, customer_po: "" });
  const [deal, setDeal] = useState(emptyDeal());
  const [busy, setBusy] = useState(false);
  const loc = head.location_id || (fin.locations && fin.locations[0] ? fin.locations[0].value : null);
  const save = async () => {
    setBusy(true);
    const res = await run(() => dmsService.saveOrder(null, { ...head, location_id: loc, ...dealPayload(deal) }));
    setBusy(false);
    if (res) {
      onSaved(res.data.id);
      onHide();
    }
  };
  return (
    <Panel
      title="New sales order"
      onHide={() => !busy && onHide()}
      width={1100}
      footer={
        <>
          <SecondaryButton onClick={onHide} disabled={busy}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} disabled={busy || !head.customer_account_id}>
            Create order
          </PrimaryButton>
        </>
      }
    >
      <div style={{ ...hint, marginBottom: 12 }}>Most orders come from an accepted quotation. A direct order is for an existing customer.</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "0 12px" }}>
        <Field label="Customer" required htmlFor="o-cust">
          <AppDropdown id="o-cust" value={head.customer_account_id} filter placeholder="Customer" options={(customers.data || []).map((c) => ({ label: `${c.name} (${c.account_number})`, value: c.id }))} onChange={(e) => setHead({ ...head, customer_account_id: e.value })} />
        </Field>
        <Field label="Location" required htmlFor="o-loc">
          <AppDropdown id="o-loc" value={loc} options={fin.locations || []} onChange={(e) => setHead({ ...head, location_id: e.value })} />
        </Field>
        <Field label="Customer PO" htmlFor="o-po">
          <input id="o-po" className="form-control" style={inputStyle} value={head.customer_po} onChange={(e) => setHead({ ...head, customer_po: e.target.value })} />
        </Field>
      </div>
      <DealEditor value={deal} onChange={setDeal} context={{ price_list_id: null, currency: "AED" }} />
    </Panel>
  );
}

/* ------------------------------ page ------------------------------ */

export default function SalesOrders() {
  const [params, setParams] = useSearchParams();
  const [filters, setFilters] = useState({ status: "", q: "" });
  const [creating, setCreating] = useState(false);
  const { data, loading, error, reload } = useLoad(() => dmsService.listOrders({ status: filters.status || undefined, open: filters.status ? undefined : 1, q: filters.q || undefined }), [filters.status, filters.q]);
  const open = params.get("id");
  const setOpen = (id) => setParams(id ? { id } : {});
  return (
    <div style={pageStyle}>
      <PageHeader title="Sales Orders" subtitle="From booking to delivery and invoice" actions={<SecondaryButton onClick={() => setCreating(true)}>Direct order</SecondaryButton>} />
      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        <div style={{ width: 200 }}>
          <AppDropdown id="ss" value={filters.status} options={STATUS} onChange={(e) => setFilters({ ...filters, status: e.value })} />
        </div>
        <input className="form-control" style={{ ...inputStyle, width: 260 }} placeholder="Number, customer, phone or PO" aria-label="Search orders" onKeyDown={(e) => e.key === "Enter" && setFilters({ ...filters, q: e.target.value.trim() })} />
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          onRowClick={(r) => setOpen(r.id)}
          empty={<EmptyState title="No orders" text="Accept a quotation to create an order." />}
          columns={[
            { label: "Number", render: (r) => <b>{r.so_number}</b> },
            { label: "Date", render: (r) => formatDate(r.order_date) },
            { label: "Customer", render: (r) => r.customer_name },
            { label: "Consultant", render: (r) => r.salesperson_name },
            { label: "Vehicles", num: true, render: (r) => r.vehicles },
            { label: "Total", num: true, render: (r) => `${fmtMoney(r.total)} ${r.currency}` },
            { label: "Status", render: (r) => <DmsBadge status={r.status} /> },
          ]}
        />
      </LoadState>
      {open ? <OrderView id={open} onHide={() => setOpen(null)} onChanged={reload} /> : null}
      {creating ? (
        <NewOrder
          onHide={() => setCreating(false)}
          onSaved={(id) => {
            reload();
            setOpen(id);
          }}
        />
      ) : null}
    </div>
  );
}
